import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { toast } from "sonner";
import { formatDay, daysInStageLabel, VERDICT_LABEL, VERDICT_COLOR } from "@/lib/format";
import { ArrowLeft, CheckCircle2, XCircle, Play,
  FileText, ListChecks, ScrollText, Activity, StickyNote, Bot,
  Upload, Check, Save, Edit3, ChevronDown, Plus, Sparkles, FileSpreadsheet,
  ListTodo,
} from "lucide-react";
import RejectDealModal from "@/components/RejectDealModal";
import RunGateModal from "@/components/RunGateModal";
import RunAgentModal from "@/components/RunAgentModal";
import MoveStageModal from "@/components/MoveStageModal";
import AdditionalDetails from "@/components/AdditionalDetails";
import DealTagsBar from "@/components/DealTagsBar";
import DealTasksTab from "@/components/DealTasksTab";
import TaskDetailPanel from "@/components/TaskDetailPanel";

import { Bell as BellIcon } from "lucide-react";
import NotificationRulesPanel from "@/components/NotificationRulesPanel";

const TABS = [
  { key: "overview", label: "Overview", icon: FileText },
  { key: "storage", label: "Deal Data Storage", icon: FileSpreadsheet },
  { key: "diligence", label: "Diligence", icon: ListChecks },
  { key: "gates", label: "Gates", icon: Activity },
  { key: "agents", label: "Agent Outputs", icon: Bot },
  { key: "rules", label: "Rules", icon: BellIcon },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "tasks", label: "Tasks", icon: ListTodo },
  { key: "flow", label: "Flow Log", icon: ScrollText },
];

function translateLabel(label, term) {
  if (!label) return label;
  return label.split(/(\s+)/).map((tok) => (/^\s+$/.test(tok) ? tok : term(tok))).join("");
}

export default function DealDetail() {
  const { dealId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const term = useTerm();
  const canAcceptReject = user?.role === "vc_admin" || user?.role === "senior_vc";
  const [deal, setDeal] = useState(null);
  const [founders, setFounders] = useState([]);
  const [stages, setStages] = useState([]);
  const [tab, setTab] = useState("overview");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && ["overview", "storage", "diligence", "gates", "agents", "rules", "notes", "tasks", "flow"].includes(t)) {
      setTab(t);
    }
  }, [searchParams]);
  const [showReject, setShowReject] = useState(false);
  const [showRunGate, setShowRunGate] = useState(false);
  const [showRunAgent, setShowRunAgent] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [stageDropdown, setStageDropdown] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/deals/${dealId}`);
    setDeal(data.deal);
    setFounders(data.founders);
    const { data: s } = await api.get(`/pipelines/${data.deal.pipeline_id}/stages`);
    setStages(s);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  if (!deal) {
    return <div className="p-10 text-[var(--cw-muted)]">Loading…</div>;
  }
  const currentStage = stages.find((s) => s.stage_id === deal.current_stage_id);
  const isReadOnly = deal.deal_status !== "active";

  const acceptDeal = async () => {
    try {
      await api.post(`/deals/${dealId}/accept`);
      toast.success(`Accepted ${deal.company_name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to accept");
    }
  };

  const submitReject = async (payload) => {
    try {
      await api.post(`/deals/${dealId}/reject`, payload);
      toast.success(`Rejected ${deal.company_name}`);
      setShowReject(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject");
    }
  };

  const submitMove = async (comment) => {
    try {
      await api.post(`/deals/${dealId}/move-stage`, {
        to_stage_id: moveTarget.stage_id, comment,
      });
      toast.success(`Moved to ${moveTarget.stage_name}`);
      setMoveTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move");
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      {/* Back */}
      <button onClick={() => nav("/pipeline")}
        className="text-sm inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="back-to-pipeline">
        <ArrowLeft size={14} /> Back to {term("Pipeline")}
      </button>

      {/* Header */}
      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="cw-h1" data-testid="deal-title">{deal.company_name}</h1>
            {deal.deal_status === "accepted" && (
              <span className="cw-badge" style={{ background: "rgba(44,154,82,0.15)", color: "#1F6E3A" }}>
                <CheckCircle2 size={12} /> Accepted
              </span>
            )}
            {deal.deal_status === "rejected" && (
              <span className="cw-badge" style={{ background: "rgba(214,69,69,0.15)", color: "#8A2A2A" }}>
                <XCircle size={12} /> Rejected
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-4 text-sm text-[var(--cw-muted)] flex-wrap">
            {deal.sector && <span>{deal.sector}</span>}
            {deal.company_domain && <span>{deal.company_domain}</span>}
            <span>Created {formatDay(deal.created_at)}</span>
            <span
              className="text-[11px] font-medium px-1.5 py-0.5 rounded"
              style={{
                background: deal.is_stagnant ? "rgba(215,60,60,0.12)" : "rgba(40,42,116,0.08)",
                color: deal.is_stagnant ? "#B02B2B" : "var(--cw-primary)",
              }}
              data-testid="deal-days-in-stage"
              title={deal.is_stagnant ? "Stagnant (past stage threshold)" : "In current stage"}
            >
              {deal.days_in_stage ?? 0}d in stage
            </span>
            <span
              className="text-[11px] font-medium px-1.5 py-0.5 rounded"
              style={{ background: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" }}
              data-testid="deal-days-old"
            >
              {deal.days_old ?? 0}d old
            </span>
          </div>

          {/* v5.7 Addition 12: custom tag bar */}
          <div className="mt-3">
            <DealTagsBar dealId={deal.deal_id} pipelineId={deal.pipeline_id} />
          </div>

          {/* Stage dropdown */}
          <div className="mt-3 relative inline-block">
            <button
              onClick={() => !isReadOnly && setStageDropdown((s) => !s)}
              disabled={isReadOnly}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-md px-3 py-1.5 border transition disabled:opacity-60"
              style={{ borderColor: "var(--cw-primary)", color: "var(--cw-primary)", background: "rgba(40,42,116,0.05)" }}
              data-testid="stage-dropdown-trigger"
            >
              {term("Stage")}: {currentStage?.stage_name || "—"}
              {!isReadOnly && <ChevronDown size={14} />}
            </button>
            <span className="ml-3 text-xs text-[var(--cw-muted)]">
              {daysInStageLabel(deal.stage_entered_at)}
            </span>
            {stageDropdown && (
              <div className="absolute z-10 mt-1 cw-card p-1 min-w-[220px]">
                {stages.map((s) => (
                  <button
                    key={s.stage_id}
                    onClick={() => {
                      setStageDropdown(false);
                      if (s.stage_id !== deal.current_stage_id) setMoveTarget(s);
                    }}
                    className={`block w-full text-left text-sm px-3 py-2 rounded-md hover:bg-[rgba(40,42,116,0.06)] ${
                      s.stage_id === deal.current_stage_id ? "font-semibold" : ""
                    }`}
                    data-testid={`stage-option-${s.stage_name}`}
                  >
                    {s.stage_name}
                    {s.stage_id === deal.current_stage_id && (
                      <span className="cw-caption ml-2">(current)</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex flex-wrap gap-2">
            <button onClick={acceptDeal}
              disabled={!canAcceptReject}
              title={canAcceptReject ? "" : `Only Senior VC and VC Admin can accept ${term("deals").toLowerCase()}`}
              className="cw-btn-secondary text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="accept-deal-btn">
              <CheckCircle2 size={14} /> Accept
            </button>
            <button onClick={() => setShowReject(true)}
              disabled={!canAcceptReject}
              title={canAcceptReject ? "" : `Only Senior VC and VC Admin can reject ${term("deals").toLowerCase()}`}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
              data-testid="reject-deal-btn">
              <XCircle size={14} /> Reject
            </button>
          </div>
        )}
        {/* v5.5 Addition 11: soft-delete (VC Admin or Deal Owner) — shown even in read-only */}
        {(user?.role === "vc_admin" || user?.user_id === deal.deal_owner_id) && (
          <button onClick={() => setShowDelete(true)}
            className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition"
            style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
            data-testid="delete-deal-btn">
            Delete {term("Deal")}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b flex gap-1 overflow-x-auto" style={{ borderColor: "var(--cw-border)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 transition ${
              tab === t.key ? "" : "text-[var(--cw-muted)] border-transparent"
            }`}
            style={tab === t.key ? { color: "var(--cw-primary)", borderColor: "var(--cw-secondary)" } : {}}
            data-testid={`deal-tab-${t.key}`}>
            <t.icon size={14} /> {translateLabel(t.label, term)}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab deal={deal} founders={founders} isReadOnly={isReadOnly} onSaved={load} />}
        {tab === "diligence" && <DiligenceTab dealId={dealId} />}
        {tab === "gates" && (
          <AgenticGatesTab dealId={dealId} isReadOnly={isReadOnly} />
        )}
        {tab === "storage" && (
          <DealDataStorageCard dealId={deal.deal_id} isReadOnly={isReadOnly} enhanced />
        )}
        {tab === "agents" && (
          <AgentOutputsTab dealId={dealId} onRunAgent={() => setShowRunAgent(true)} isReadOnly={isReadOnly} />
        )}
        {tab === "notes" && <NotesTab dealId={dealId} />}
        {tab === "rules" && (
          <div className="cw-card p-5">
            <NotificationRulesPanel
              scope="deal"
              dealId={dealId}
              canManage={!isReadOnly}
            />
          </div>
        )}
        {tab === "tasks" && (
          <DealTasksTab
            dealId={dealId}
            pipelineId={deal.pipeline_id}
            isReadOnly={isReadOnly}
          />
        )}
        {tab === "flow" && <FlowLogTab dealId={dealId} />}
      </div>

      {/* Modals */}
      {showReject && (
        <RejectDealModal deal={deal} onCancel={() => setShowReject(false)} onConfirm={submitReject} />
      )}
      {showRunGate && (
        <RunGateModal deal={deal} onClose={() => setShowRunGate(false)} onCompleted={load} />
      )}
      {showRunAgent && (
        <RunAgentModal deal={deal} onClose={() => setShowRunAgent(false)} onCompleted={load} />
      )}
      {moveTarget && (
        <MoveStageModal
          deal={deal}
          fromStage={currentStage}
          toStage={moveTarget}
          onCancel={() => setMoveTarget(null)}
          onConfirm={submitMove}
        />
      )}
      {showDelete && (
        <DeleteDealModal
          deal={deal}
          onCancel={() => setShowDelete(false)}
          onConfirmed={() => { setShowDelete(false); nav("/pipeline"); }}
        />
      )}
    </div>
  );
}

/* -------------------- Overview -------------------- */
const ROLE_LABEL = {
  vc_admin: "VC Admin",
  it_admin: "IT Admin",
  senior_vc: "Partner",
  junior_vc: "Analyst",
};

function OverviewTab({ deal, founders, isReadOnly, onSaved }) {
  const term = useTerm();
  const [edit, setEdit] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [form, setForm] = useState({
    company_name: deal.company_name,
    company_description: deal.company_description || "",
    company_domain: deal.company_domain || "",
    company_linkedin_url: deal.company_linkedin_url || "",
    sector: deal.sector || "",
    round_size: deal.round_size || "",
    pre_money_valuation: deal.pre_money_valuation || "",
    arr: deal.arr || "",
    burn: deal.burn || "",
    source: deal.source || "",
    deal_owner_id: deal.deal_owner_id || "",
  });

  useEffect(() => {
    api.get("/team-members").then(({ data }) => setTeamMembers(data));
  }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      // Explicit clear when user picks "Unassigned"
      if (payload.deal_owner_id === "") {
        payload.clear_owner = true;
        delete payload.deal_owner_id;
      }
      await api.patch(`/deals/${deal.deal_id}`, payload);
      toast.success("Deal updated");
      setEdit(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const ownerMember = teamMembers.find((m) => m.user_id === deal.deal_owner_id);
  const ownerLabel = (m) => m.job_title || ROLE_LABEL[m.role] || m.role;
  const ownerDisplay = ownerMember
    ? `${ownerMember.full_name} · ${ownerLabel(ownerMember)}`
    : deal.deal_owner_id
    ? "Loading owner…"
    : "Unassigned";

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-6">
        <div className="cw-card p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="cw-h3">Company</h3>
            {!isReadOnly && (
              edit ? (
                <button onClick={save} className="cw-btn-primary text-sm inline-flex items-center gap-1" data-testid="save-deal-btn">
                  <Save size={14} /> Save
                </button>
              ) : (
                <button onClick={() => setEdit(true)} className="cw-btn-ghost text-sm inline-flex items-center gap-1" data-testid="edit-deal-btn">
                  <Edit3 size={14} /> Edit
                </button>
              )
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FieldRO label="Company" edit={edit} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="ov-company-name" />
            <FieldRO label="Domain" edit={edit} value={form.company_domain} onChange={(v) => setForm({ ...form, company_domain: v })} testid="ov-domain" />
            {/* v5.8 A21 */}
            <FieldRO label="Company LinkedIn" edit={edit} value={form.company_linkedin_url} onChange={(v) => setForm({ ...form, company_linkedin_url: v })} testid="ov-company-linkedin" />
            <FieldRO label="Sector" edit={edit} value={form.sector} onChange={(v) => setForm({ ...form, sector: v })} testid="ov-sector" />
            <FieldRO label="Source" edit={edit} value={form.source} onChange={(v) => setForm({ ...form, source: v })} testid="ov-source" />
          </div>
          <div className="mt-4">
            <FieldRO label="Description" edit={edit} value={form.company_description} onChange={(v) => setForm({ ...form, company_description: v })} multiline testid="ov-desc" />
          </div>
          <div className="mt-4">
            <div className="cw-caption">{term("Deal")} Owner</div>
            {edit ? (
              <select value={form.deal_owner_id}
                onChange={(e) => setForm({ ...form, deal_owner_id: e.target.value })}
                className="w-full h-9 px-3 rounded-md border text-sm bg-white mt-1"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid="ov-owner-select">
                <option value="">Unassigned</option>
                {teamMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name} · {ownerLabel(m)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm mt-0.5" data-testid="ov-owner-display">
                {ownerDisplay}
              </div>
            )}
          </div>
        </div>

        <div className="cw-card p-5">
          <h3 className="cw-h3 mb-3">Round & Traction</h3>
          <div className="grid grid-cols-2 gap-4">
            <FieldRO label="Round Size" edit={edit} value={form.round_size} onChange={(v) => setForm({ ...form, round_size: v })} placeholder="e.g. 5 Cr" testid="ov-round" />
            <FieldRO label="Pre-money Valuation" edit={edit} value={form.pre_money_valuation} onChange={(v) => setForm({ ...form, pre_money_valuation: v })} placeholder="e.g. 40 Cr or 50 Cr" testid="ov-premoney" />
            <FieldRO label="ARR" edit={edit} value={form.arr} onChange={(v) => setForm({ ...form, arr: v })} testid="ov-arr" />
            <FieldRO label="Monthly Burn" edit={edit} value={form.burn} onChange={(v) => setForm({ ...form, burn: v })} testid="ov-burn" />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="cw-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="cw-h3">{term("Founders")}</h3>
            {!isReadOnly && (
              <FounderAddButton dealId={deal.deal_id} onAdded={onSaved} />
            )}
          </div>
          <div className="space-y-4">
            {founders.map((f) => (
              <FounderRow
                key={f.founder_id}
                founder={f}
                edit={edit}
                isReadOnly={isReadOnly}
                canRemove={founders.length > 1}
                onSaved={onSaved}
              />
            ))}
          </div>
        </div>

        {/* v5.5 Addition 18: Pitch Deck upload */}
        <PitchDeckCard dealId={deal.deal_id} isReadOnly={isReadOnly} />

        {/* v5.7 Addition 2: custom fields */}
        <AdditionalDetails dealId={deal.deal_id} pipelineId={deal.pipeline_id} />

        {deal.deal_status === "rejected" && (
          <div className="cw-card p-5">
            <h3 className="cw-h3 mb-3" style={{ color: "var(--cw-error)" }}>Rejection</h3>
            <dl className="text-sm space-y-2">
              <div><dt className="cw-caption">Reason</dt><dd>{deal.rejection_reason}</dd></div>
              <div><dt className="cw-caption">Sub-category</dt><dd>{deal.rejection_subcategory?.replace("_", " ")}</dd></div>
              <div><dt className="cw-caption">By</dt><dd>{deal.rejected_by_type === "vc_firm" ? "VC Firm" : term("Founder")}</dd></div>
              {deal.rejection_note && <div><dt className="cw-caption">Note</dt><dd>{deal.rejection_note}</dd></div>}
              <div><dt className="cw-caption">Date</dt><dd>{formatDay(deal.rejected_at)}</dd></div>
            </dl>
          </div>
        )}
        {deal.deal_status === "accepted" && (
          <div className="cw-card p-5">
            <h3 className="cw-h3 mb-2" style={{ color: "var(--cw-success)" }}>Accepted</h3>
            <div className="text-sm text-[var(--cw-muted)]">On {formatDay(deal.accepted_at)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRO({ label, edit, value, onChange, multiline, testid, placeholder }) {
  return (
    <div>
      <div className="cw-caption">{label}</div>
      {edit ? (
        multiline ? (
          <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
            className="w-full px-3 py-2 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }} data-testid={testid}
            placeholder={placeholder} />
        ) : (
          <input value={value || ""} onChange={(e) => onChange(e.target.value)}
            className="w-full h-9 px-3 rounded-md border text-sm mt-1"
            style={{ borderColor: "var(--cw-border)" }} data-testid={testid}
            placeholder={placeholder} />
        )
      ) : (
        <div className="text-sm mt-0.5">{value || "—"}</div>
      )}
    </div>
  );
}


function FounderRow({ founder, edit, isReadOnly, canRemove, onSaved }) {
  const term = useTerm();
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    first_name: founder.first_name || "",
    last_name: founder.last_name || "",
    role: founder.role || "",
    founder_email: founder.founder_email || "",
    founder_linkedin_url: founder.founder_linkedin_url || "",
  });
  const [saving, setSaving] = useState(false);
  const initial = (founder.first_name?.[0] || "").toUpperCase();

  const saveAll = async () => {
    if (!form.first_name.trim()) {
      toast.error("First name is required");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/founders/${founder.founder_id}`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role.trim() || null,
        founder_email: form.founder_email.trim() || null,
        founder_linkedin_url: form.founder_linkedin_url.trim() || null,
      });
      toast.success(`${term("Founder")} updated`);
      setEditMode(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = async () => {
    try {
      await api.patch(`/founders/${founder.founder_id}/set-primary`);
      toast.success(`Primary ${term("founder").toLowerCase()} updated`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${term("founder").toLowerCase()} ${founder.first_name} ${founder.last_name || ""}?`)) return;
    try {
      await api.delete(`/founders/${founder.founder_id}`);
      toast.success(`${term("Founder")} removed`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const inp = "w-full h-8 px-2 rounded-md border text-xs";
  const border = { borderColor: "var(--cw-border)" };

  if (editMode) {
    return (
      <div className="flex items-start gap-3 border rounded-md p-3"
        style={{ borderColor: "var(--cw-primary)" }}
        data-testid={`founder-edit-${founder.founder_id}`}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm shrink-0"
          style={{ background: "rgba(40,42,116,0.1)", color: "var(--cw-primary)" }}>
          {initial}
        </div>
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name *" className={inp} style={border}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              data-testid={`founder-edit-fn-${founder.founder_id}`} />
            <input placeholder="Last name" className={inp} style={border}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <input placeholder="Role (e.g. CEO)" className={inp} style={border}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid={`founder-edit-role-${founder.founder_id}`} />
          <input placeholder="Email" type="email" className={inp} style={border}
            value={form.founder_email}
            onChange={(e) => setForm({ ...form, founder_email: e.target.value })}
            data-testid={`founder-edit-email-${founder.founder_id}`} />
          <input placeholder="LinkedIn URL" className={inp} style={border}
            value={form.founder_linkedin_url}
            onChange={(e) => setForm({ ...form, founder_linkedin_url: e.target.value })}
            data-testid={`founder-edit-linkedin-${founder.founder_id}`} />
          <div className="flex gap-2 mt-2">
            <button onClick={saveAll} disabled={saving}
              className="cw-btn-primary text-xs h-7 px-3"
              data-testid={`founder-edit-save-${founder.founder_id}`}>
              {saving ? "…" : "Save"}
            </button>
            <button onClick={() => setEditMode(false)}
              className="cw-btn-ghost text-xs h-7 px-3">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm shrink-0"
        style={{ background: "rgba(40,42,116,0.1)", color: "var(--cw-primary)" }}>
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-2">
          <span>{founder.first_name} {founder.last_name}</span>
          {founder.is_primary && (
            <span
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
              style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
              data-testid={`founder-primary-${founder.founder_id}`}
            >
              PRIMARY
            </span>
          )}
        </div>
        {founder.role && <div className="cw-caption uppercase">{founder.role}</div>}
        {founder.founder_email && (
          <div className="text-xs text-[var(--cw-muted)] mt-0.5 break-all">{founder.founder_email}</div>
        )}
        {founder.founder_linkedin_url ? (
          <a href={founder.founder_linkedin_url.startsWith("http")
                ? founder.founder_linkedin_url
                : `https://${founder.founder_linkedin_url}`}
            target="_blank" rel="noreferrer"
            className="text-xs mt-0.5 underline break-all inline-block"
            style={{ color: "var(--cw-primary)" }}
            data-testid={`founder-linkedin-link-${founder.founder_id}`}>
            {founder.founder_linkedin_url}
          </a>
        ) : null}
        {!isReadOnly && (
          <div className="mt-2 flex gap-2 flex-wrap">
            <button onClick={() => setEditMode(true)}
              className="cw-btn-ghost text-[10px]"
              data-testid={`founder-edit-btn-${founder.founder_id}`}>
              Edit
            </button>
            {!founder.is_primary && (
              <button onClick={setPrimary} className="cw-btn-ghost text-[10px]"
                data-testid={`founder-set-primary-${founder.founder_id}`}>
                Set primary
              </button>
            )}
            {canRemove && (
              <button onClick={remove} className="cw-btn-ghost text-[10px] text-[var(--cw-error)]"
                data-testid={`founder-remove-${founder.founder_id}`}>
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* -------------------- Founder Add (v5.5 #14) -------------------- */
function FounderAddButton({ dealId, onAdded }) {
  const term = useTerm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    role: "",
    founder_linkedin_url: "",
    founder_email: "",
    is_primary: false,
  });
  const [busy, setBusy] = useState(false);
  const border = { borderColor: "var(--cw-border)" };
  const inp = "w-full h-8 px-2 rounded-md border text-xs mt-0.5";

  const submit = async () => {
    if (!form.first_name.trim()) {
      toast.error("First name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        deal_id: dealId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role.trim() || null,
        founder_linkedin_url: form.founder_linkedin_url.trim() || null,
        founder_email: form.founder_email.trim() || null,
        is_primary: form.is_primary,
      };
      await api.post(`/deals/${dealId}/founders`, payload);
      toast.success(`${term("Founder")} added`);
      setOpen(false);
      setForm({ first_name: "", last_name: "", role: "", founder_linkedin_url: "", founder_email: "", is_primary: false });
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="cw-btn-ghost text-xs inline-flex items-center gap-1"
        data-testid="add-founder-btn">
        <Plus size={12} /> Add
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="add-founder-modal">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-3 border-b" style={border}>
              <h3 className="cw-h3">Add {term("founder").toLowerCase()}</h3>
            </div>
            <div className="p-5 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="cw-caption">First name *</div>
                  <input className={inp} style={border} value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="new-founder-fn" />
                </div>
                <div>
                  <div className="cw-caption">Last name</div>
                  <input className={inp} style={border} value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="new-founder-ln" />
                </div>
              </div>
              <div>
                <div className="cw-caption">Role</div>
                <input className={inp} style={border} placeholder="e.g. CEO, CTO"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })} />
              </div>
              <div>
                <div className="cw-caption">Email</div>
                <input className={inp} style={border} type="email"
                  value={form.founder_email}
                  onChange={(e) => setForm({ ...form, founder_email: e.target.value })} />
              </div>
              <div>
                <div className="cw-caption">LinkedIn</div>
                <input className={inp} style={border}
                  value={form.founder_linkedin_url}
                  onChange={(e) => setForm({ ...form, founder_linkedin_url: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-xs mt-2">
                <input type="checkbox" checked={form.is_primary}
                  onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
                Set as primary founder
              </label>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2" style={border}>
              <button onClick={() => setOpen(false)} className="cw-btn-ghost text-xs">Cancel</button>
              <button onClick={submit} disabled={busy}
                className="cw-btn-primary text-xs"
                data-testid="new-founder-submit">
                {busy ? "Adding…" : "Add founder"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* -------------------- Pitch Deck Card (v5.5 #18) -------------------- */
function PitchDeckCard({ dealId, isReadOnly }) {
  const term = useTerm();
  const nav = useNavigate();
  const [deck, setDeck] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  const load = () => {
    api.get(`/deals/${dealId}/pitch-deck`)
      .then(({ data }) => setDeck(data))
      .catch(() => setDeck(null));
  };
  useEffect(() => { load(); }, [dealId]); // eslint-disable-line

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ct = f.type;
    const ok = ["application/pdf",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-powerpoint"].includes(ct);
    if (!ok) {
      toast.error("Only PDF or PPT/PPTX allowed");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error(`${term("Pitch Deck")} must be under 50MB`);
      return;
    }
    setUploading(true);
    try {
      // Read as base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          const base64 = String(dataUrl).split(",")[1] || "";
          await api.post(`/deals/${dealId}/pitch-deck`, {
            filename: f.name,
            content_type: ct,
            data_base64: base64,
          });
          toast.success(`${term("Pitch Deck")} uploaded`);
          load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(f);
    } catch {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${term("pitch deck").toLowerCase()}?`)) return;
    try {
      await api.delete(`/deals/${dealId}/pitch-deck`);
      toast.success("Removed");
      setDeck(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const download = () => {
    if (!deck?.data_base64) return;
    const link = document.createElement("a");
    link.href = `data:${deck.content_type};base64,${deck.data_base64}`;
    link.download = deck.filename;
    link.click();
  };

  // v5.9 A2: Upload Deck & Auto-Populate — starts a deck_extraction run
  // and takes the user to the Field Mapping screen for approval.
  const uploadAndAutoPopulate = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ct = f.type;
    const ok = ["application/pdf"].includes(ct);
    if (!ok) {
      toast.error("Auto-populate currently supports PDF only");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File must be under 50MB");
      return;
    }
    setAutoBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          const base64 = String(dataUrl).split(",")[1] || "";
          const { data } = await api.post(`/deals/${dealId}/deck-extraction`, {
            filename: f.name,
            file_type: (f.name.split(".").pop() || "").toLowerCase(),
            data_base64: base64,
          });
          toast.success("Extraction started");
          nav(`/agent-runs/${data.run_id}/map`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Extraction failed to start");
        } finally {
          setAutoBusy(false);
        }
      };
      reader.readAsDataURL(f);
    } catch {
      setAutoBusy(false);
    }
  };

  const sizeKb = deck?.size_bytes ? Math.round(deck.size_bytes / 1024) : 0;
  const sizeLabel = sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;

  return (
    <div className="cw-card p-5" data-testid="pitch-deck-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="cw-h3">{term("Pitch Deck")}</h3>
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <label className="cw-btn-secondary text-xs inline-flex items-center gap-1 cursor-pointer"
                   title="Upload a PDF and auto-populate deal fields with AI">
              <Sparkles size={12} />
              {autoBusy ? "Starting…" : "Auto-populate"}
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={uploadAndAutoPopulate}
                disabled={autoBusy}
                data-testid="auto-populate-input"
              />
            </label>
            <label className="cw-btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
              <Upload size={12} />
              {deck ? "Replace" : "Upload"}
              <input
                type="file"
                hidden
                accept="application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint"
                onChange={onFile}
                data-testid="pitch-deck-input"
              />
            </label>
          </div>
        )}
      </div>
      {uploading && <div className="text-xs text-[var(--cw-muted)]">Uploading…</div>}
      {deck ? (
        <div className="text-sm space-y-1">
          <div className="font-medium truncate">{deck.filename}</div>
          <div className="text-xs text-[var(--cw-muted)]">{sizeLabel}</div>
          <div className="flex gap-2 mt-2">
            <button onClick={download} className="cw-btn-primary text-xs"
              data-testid="pitch-deck-download">
              Download
            </button>
            {!isReadOnly && (
              <button onClick={remove} className="cw-btn-ghost text-xs text-[var(--cw-error)]"
                data-testid="pitch-deck-remove">
                Remove
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-xs text-[var(--cw-muted)] italic">
          No {term("pitch deck").toLowerCase()} uploaded. Upload a PDF or PPT/PPTX (max 50MB).
        </div>
      )}
    </div>
  );
}


/* -------------------- v5.10 A1: Deal Data Storage -------------------- */
function DealDataStorageCard({ dealId, isReadOnly, enhanced }) {
  const term = useTerm();
  const [files, setFiles] = useState([]);
  const [pasting, setPasting] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [groupBySource, setGroupBySource] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/deals/${dealId}/files`);
      setFiles(data);
    } catch { /* ignore */ }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const savePaste = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/paste-note`, form);
      toast.success("Note added");
      setForm({ title: "", content: "" });
      setPasting(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (file) => {
    try {
      const { data } = await api.get(`/deals/${dealId}/files/${file.file_id}`);
      if (!data.data_base64) { toast.error("No file content"); return; }
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${data.data_base64}`;
      link.download = data.filename;
      link.click();
    } catch { toast.error("Download failed"); }
  };

  const sourceBadge = (src) => {
    const cfg = {
      email_followup: { label: "Email", bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" },
      meeting_transcript: { label: "Transcript", bg: "rgba(44,154,82,0.14)", color: "#1F6E3A" },
      manual_note: { label: "Note", bg: "rgba(243,191,92,0.18)", color: "#8A6510" },
      manual_upload: { label: "Upload", bg: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" },
      email_ingestion: { label: "Email", bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" },
    }[src] || { label: src || "—", bg: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" };
    return (
      <span className="cw-badge" style={{ background: cfg.bg, color: cfg.color }}>
        {cfg.label}
      </span>
    );
  };

  const border = { borderColor: "var(--cw-border)" };
  return (
    <div className="cw-card p-5" data-testid="deal-data-storage-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="cw-h3">{term("Deal")} Data Storage</h3>
          <p className="text-xs text-[var(--cw-muted)] mt-0.5">
            {files.length} document{files.length !== 1 ? "s" : ""} — files, emails, transcripts, notes
          </p>
        </div>
        {!isReadOnly && (
          <button onClick={() => setPasting((v) => !v)}
            className="cw-btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="paste-content-btn">
            {pasting ? "Cancel" : "+ Paste Content"}
          </button>
        )}
      </div>

      {pasting && (
        <div className="rounded-md border p-3 mb-3 space-y-2" style={border}>
          <input placeholder="Title (e.g. Call notes with Anna, 12 Feb)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full h-9 px-3 rounded-md border text-sm" style={border}
            data-testid="paste-title-input" />
          <textarea placeholder="Paste WhatsApp exchange, call notes, transcript…"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            className="w-full min-h-[120px] px-3 py-2 rounded-md border text-sm" style={border}
            data-testid="paste-content-input" />
          <div className="text-right">
            <button onClick={savePaste} disabled={busy}
              className="cw-btn-primary text-xs"
              data-testid="paste-save-btn">
              {busy ? "Saving…" : "Save note"}
            </button>
          </div>
        </div>
      )}

      {enhanced && files.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filename…"
            className="h-9 px-3 rounded-md border text-sm flex-1 min-w-[200px]"
            style={border} data-testid="storage-search" />
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
            className="h-9 px-2 rounded-md border text-xs bg-white" style={border}
            data-testid="storage-filter">
            <option value="all">All sources</option>
            <option value="manual_upload">Uploads</option>
            <option value="email_ingestion">Deck (email)</option>
            <option value="email_followup">Email</option>
            <option value="meeting_transcript">Transcript</option>
            <option value="manual_note">Note</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="h-9 px-2 rounded-md border text-xs bg-white" style={border}
            data-testid="storage-sort">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="filename">Filename A–Z</option>
            <option value="size">Largest first</option>
          </select>
          <label className="text-xs inline-flex items-center gap-1 text-[var(--cw-muted)]">
            <input type="checkbox" checked={groupBySource}
              onChange={(e) => setGroupBySource(e.target.checked)}
              data-testid="storage-group" />
            Group by source
          </label>
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-xs text-[var(--cw-muted)] italic">
          No documents yet. Forward emails to your intake mailbox, or paste content above.
        </div>
      ) : (
        <StorageFileList files={files} enhanced={enhanced} search={search}
          sourceFilter={sourceFilter} sortBy={sortBy}
          groupBySource={groupBySource && enhanced}
          onDownload={download} sourceBadge={sourceBadge} />
      )}
    </div>
  );
}

function StorageFileList({ files, enhanced, search, sourceFilter, sortBy, groupBySource, onDownload, sourceBadge }) {
  const filtered = files
    .filter((f) => sourceFilter === "all" || (f.source || "").toLowerCase() === sourceFilter)
    .filter((f) => !search || (f.filename || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
      if (sortBy === "filename") return (a.filename || "").localeCompare(b.filename || "");
      if (sortBy === "size") return (b.size_bytes || 0) - (a.size_bytes || 0);
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  if (filtered.length === 0) {
    return <div className="text-xs text-[var(--cw-muted)] italic">No documents match your filters.</div>;
  }
  const groups = groupBySource
    ? filtered.reduce((acc, f) => {
        const k = f.source || "other";
        (acc[k] = acc[k] || []).push(f);
        return acc;
      }, {})
    : { _all: filtered };
  const border = { borderColor: "var(--cw-border)" };
  const groupLabels = {
    manual_upload: "Uploads", email_ingestion: "Decks (via email)",
    email_followup: "Email correspondence", meeting_transcript: "Meeting transcripts",
    manual_note: "Notes", other: "Other", _all: "All",
  };
  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([g, gfiles]) => (
        <div key={g}>
          {groupBySource && (
            <div className="text-[10px] uppercase tracking-widest text-[var(--cw-muted)] mb-2">
              {groupLabels[g] || g} · {gfiles.length}
            </div>
          )}
          <div className="space-y-2">
            {gfiles.map((f) => (
              <div key={f.file_id}
                className="flex items-center justify-between px-3 py-2 rounded-md border" style={border}
                data-testid={`storage-file-${f.file_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {sourceBadge(f.source)}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{f.filename}</div>
                    <div className="text-[10px] text-[var(--cw-muted)]">
                      {(f.file_type || "").toUpperCase()} · {formatBytes(f.size_bytes)} · {f.created_at}
                    </div>
                  </div>
                </div>
                <button onClick={() => onDownload(f)} className="cw-btn-ghost text-xs">
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}


/* -------------------- Diligence -------------------- */
function DiligenceTab({ dealId }) {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [proofType, setProofType] = useState("link");
  const [proofValue, setProofValue] = useState("");

  const load = useCallback(() => {
    api.get(`/deals/${dealId}/checklist`).then(({ data }) => setItems(data));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item, newVal) => {
    try {
      await api.patch(`/checklist-items/${item.checklist_item_id}/complete`, { is_completed: newVal });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const addProof = async (item) => {
    if (!proofValue.trim()) return;
    try {
      await api.post(`/checklist-items/${item.checklist_item_id}/proofs`, {
        proof_type: proofType,
        link_url: proofType === "link" ? proofValue.trim() : null,
        text_note: proofType === "text" ? proofValue.trim() : null,
      });
      toast.success("Proof added");
      setProofValue("");
      load();
    } catch (err) {
      toast.error("Failed to add proof");
    }
  };

  if (items.length === 0) {
    return <div className="cw-card p-8 text-center text-[var(--cw-muted)]">No checklist items for this stage.</div>;
  }

  return (
    <div className="space-y-2" data-testid="diligence-checklist">
      {items.map((item) => {
        const hasProof = (item.proofs || []).length > 0;
        const disabled = item.proof_required && !hasProof && !item.is_completed;
        return (
          <div key={item.checklist_item_id} className="cw-card p-4">
            <div className="flex items-start gap-3">
              <button
                onClick={() => !disabled && toggle(item, !item.is_completed)}
                disabled={disabled}
                title={disabled ? "Upload proof before completing this item" : ""}
                className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 transition ${
                  item.is_completed ? "" : ""
                } disabled:cursor-not-allowed disabled:opacity-40`}
                style={{
                  borderColor: item.is_completed ? "var(--cw-primary)" : "var(--cw-border)",
                  background: item.is_completed ? "var(--cw-primary)" : "white",
                }}
                data-testid={`checkbox-${item.checklist_item_id}`}
              >
                {item.is_completed && <Check size={12} color="white" />}
              </button>
              <div className="flex-1">
                <div className={`text-sm ${item.is_completed ? "line-through text-[var(--cw-muted)]" : ""}`}>
                  {item.criteria_text}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {item.item_type === "required" && (
                    <span className="cw-caption uppercase" style={{ color: "var(--cw-error)" }}>required</span>
                  )}
                  {item.proof_required && (
                    <span className="cw-caption">
                      {hasProof ? `${item.proofs.length} proof(s)` : "proof required"}
                    </span>
                  )}
                </div>
                {(item.proofs || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {item.proofs.map((p) => (
                      <div key={p.proof_id} className="text-xs text-[var(--cw-muted)] flex items-center gap-2">
                        <FileText size={12} />
                        {p.proof_type === "link" ? (
                          <a href={p.link_url} target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--cw-primary)" }}>
                            {p.link_url}
                          </a>
                        ) : (
                          <span>{p.text_note}</span>
                        )}
                        <span>· {formatDay(p.uploaded_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setExpandedId(expandedId === item.checklist_item_id ? null : item.checklist_item_id)}
                  className="text-xs inline-flex items-center gap-1 mt-2"
                  style={{ color: "var(--cw-primary)" }}
                  data-testid={`add-proof-toggle-${item.checklist_item_id}`}
                >
                  <Upload size={12} /> Add proof
                </button>
                {expandedId === item.checklist_item_id && (
                  <div className="mt-2 flex gap-2 items-center">
                    <select value={proofType} onChange={(e) => setProofType(e.target.value)}
                      className="h-8 px-2 rounded-md border text-xs"
                      style={{ borderColor: "var(--cw-border)" }}
                      data-testid={`proof-type-${item.checklist_item_id}`}>
                      <option value="link">Link</option>
                      <option value="text">Text note</option>
                    </select>
                    <input value={proofValue} onChange={(e) => setProofValue(e.target.value)}
                      placeholder={proofType === "link" ? "https://…" : "Short note…"}
                      className="h-8 px-2 rounded-md border text-xs flex-1"
                      style={{ borderColor: "var(--cw-border)" }}
                      data-testid={`proof-value-${item.checklist_item_id}`} />
                    <button onClick={() => addProof(item)}
                      className="cw-btn-primary text-xs h-8 px-3"
                      data-testid={`save-proof-${item.checklist_item_id}`}>
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------- Gate Scores -------------------- */
function GateScoresTab({ dealId, onRunGate, isReadOnly }) {
  const [runs, setRuns] = useState([]);
  const load = useCallback(() => {
    api.get(`/deals/${dealId}/gate-runs`).then(({ data }) => setRuns(data));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="cw-caption">Past gate runs on this deal</div>
        {!isReadOnly && (
          <button onClick={onRunGate} className="cw-btn-primary text-sm inline-flex items-center gap-1" data-testid="run-gate-btn">
            <Play size={14} /> Run Gate
          </button>
        )}
      </div>
      {runs.length === 0 ? (
        <div className="cw-card p-8 text-center text-[var(--cw-muted)]">No gates run yet.</div>
      ) : (
        <div className="space-y-3">
          {runs.map((r) => (
            <div key={r.run_id} className="cw-card p-4" data-testid={`gate-run-${r.gate_name}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold">{r.gate_name}</div>
                  <div className="text-xs text-[var(--cw-muted)] mt-1">
                    Run by {r.triggered_by_name} · {formatDay(r.completed_at)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold" style={{ color: VERDICT_COLOR[r.verdict] }}>
                    {VERDICT_LABEL[r.verdict]}
                  </div>
                  {r.total_score !== null && r.total_score !== undefined && (
                    <div className="text-xs text-[var(--cw-muted)]">Score: {r.total_score}/100</div>
                  )}
                </div>
              </div>
              <div className="mt-3 border-t pt-3 space-y-1" style={{ borderColor: "var(--cw-border)" }}>
                {r.scores.map((s) => (
                  <div key={s.score_id} className="flex justify-between text-xs">
                    <span className="text-[var(--cw-muted)]">{s.criteria_text}</span>
                    <span className="font-medium">
                      {s.criteria_type === "scored" && `${s.score_value} / ${s.max_score}`}
                      {s.criteria_type === "pass_fail" && (s.pass_fail_result ? "Pass" : "Fail")}
                      {s.criteria_type === "rubric" && s.rubric_selection}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Notes -------------------- */
function NotesTab({ dealId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(() => {
    api.get(`/deals/${dealId}/notes`).then(({ data }) => setNotes(data));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    await api.post(`/deals/${dealId}/notes`, { content: text.trim() });
    setText("");
    load();
  };

  return (
    <div className="max-w-2xl">
      <div className="cw-card p-4">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
          placeholder="Add an internal note…"
          className="w-full px-3 py-2 rounded-md border text-sm outline-none"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="note-input" />
        <div className="flex justify-end mt-2">
          <button onClick={add} className="cw-btn-primary text-sm" disabled={!text.trim()} data-testid="add-note-btn">
            Add Note
          </button>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {notes.map((n) => (
          <div key={n.note_id} className="cw-card p-4">
            <div className="text-sm whitespace-pre-wrap">{n.content}</div>
            <div className="cw-caption mt-2">
              {n.created_by_name} · {formatDay(n.created_at)}
            </div>
          </div>
        ))}
        {notes.length === 0 && (
          <div className="text-sm text-[var(--cw-muted)] text-center py-6">No notes yet.</div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Flow Log -------------------- */
function FlowLogTab({ dealId }) {
  const [logs, setLogs] = useState([]);
  const [openTaskId, setOpenTaskId] = useState(null);
  useEffect(() => {
    api.get(`/deals/${dealId}/flow-log`).then(({ data }) => setLogs(data));
  }, [dealId]);

  const reloadLogs = () => {
    api.get(`/deals/${dealId}/flow-log`).then(({ data }) => setLogs(data));
  };

  return (
    <div className="max-w-2xl">
      <div className="cw-caption mb-3">Most recent events</div>
      <div className="space-y-2">
        {logs.map((l) => {
          const isTaskEvent = typeof l.event_type === "string" && l.event_type.startsWith("task_");
          const isTaskDeleted = l.event_type === "task_deleted";
          const canOpenTask = isTaskEvent && !isTaskDeleted && l.detail_task_id;
          return (
            <div
              key={l.log_id}
              className={`cw-card p-3 flex items-start gap-3 ${canOpenTask ? "cursor-pointer hover:bg-[rgba(40,42,116,0.03)]" : ""}`}
              onClick={canOpenTask ? () => setOpenTaskId(l.detail_task_id) : undefined}
              data-testid={`flowlog-row-${l.log_id}`}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: isTaskEvent ? "rgba(243,191,92,0.18)" : "rgba(40,42,116,0.08)",
                  color: isTaskEvent ? "#8A6510" : "var(--cw-primary)",
                }}>
                {isTaskEvent ? <ListTodo size={14} /> : <Activity size={14} />}
              </div>
              <div className="flex-1">
                <div className="text-sm">{l.event_summary}</div>
                {l.event_type === "deal_stage_changed" && l.detail_comment && (
                  <div
                    className="mt-1 text-xs italic px-2 py-1 rounded"
                    style={{ background: "rgba(40,42,116,0.05)", color: "var(--cw-primary-dark)" }}
                    data-testid={`flowlog-comment-${l.log_id}`}
                  >
                    “{l.detail_comment}”
                  </div>
                )}
                <div className="cw-caption mt-1">
                  {l.actor_name} · {formatDay(l.created_at)} · {l.actor_type}
                </div>
              </div>
            </div>
          );
        })}
        {logs.length === 0 && (
          <div className="text-sm text-[var(--cw-muted)] text-center py-6">No events.</div>
        )}
      </div>

      {openTaskId && (
        <TaskDetailPanel
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
          onChanged={reloadLogs}
        />
      )}
    </div>
  );
}


/* -------------------- Agent Outputs -------------------- */
function AgentOutputsTab({ dealId, onRunAgent, isReadOnly }) {
  const [runs, setRuns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => {
    api.get(`/deals/${dealId}/agent-runs`).then(({ data }) => setRuns(data));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="cw-caption">Past agent runs on this deal</div>
        {!isReadOnly && (
          <button onClick={onRunAgent}
            className="cw-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="run-agent-btn">
            <Bot size={14} /> Run Agent
          </button>
        )}
      </div>
      {runs.length === 0 ? (
        <div className="cw-card p-8 text-center text-[var(--cw-muted)]">
          No agent runs yet. Try running an agent to get LLM-powered analysis.
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((r) => (
            <div key={r.run_id} className="cw-card p-4" data-testid={`agent-run-${r.agent_name}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Bot size={14} style={{ color: "var(--cw-primary)" }} />
                    <div className="font-semibold text-sm">{r.agent_name}</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-xs text-[var(--cw-muted)] mt-1">
                    {r.model_provider} · {r.model_name} · Run by {r.triggered_by_name} · {formatDay(r.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => setExpanded(expanded === r.run_id ? null : r.run_id)}
                  className="cw-btn-ghost text-xs"
                  data-testid={`toggle-run-${r.run_id}`}
                >
                  {expanded === r.run_id ? "Collapse" : "View"}
                </button>
              </div>
              {expanded === r.run_id && (
                <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--cw-border)" }}>
                  {r.status === "failed" ? (
                    <div className="text-sm" style={{ color: "var(--cw-error)" }}>
                      Run failed: {r.error_message}
                    </div>
                  ) : r.status === "awaiting_user_input" || r.interaction_mode === "interactive" ? (
                    // v5.7 Addition 15: interactive conversation panel
                    <AgentConversation runId={r.run_id} initialOutput={r.output_text} />
                  ) : (
                    <div className="text-sm whitespace-pre-wrap"
                      style={{ color: "var(--cw-body)" }}
                      data-testid={`run-output-${r.run_id}`}>
                      {r.output_text || "No output"}
                    </div>
                  )}
                  {/* v5.7 Addition 16: multi-format output files */}
                  <RunOutputFiles runId={r.run_id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: { bg: "rgba(44,154,82,0.15)", color: "#1F6E3A", text: "Completed" },
    running: { bg: "rgba(224,161,0,0.15)", color: "#7A5900", text: "Running" },
    failed: { bg: "rgba(214,69,69,0.15)", color: "#8A2A2A", text: "Failed" },
  };
  const cfg = map[status] || map.completed;
  return (
    <span className="cw-badge" style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.text}
    </span>
  );
}


/* -------------------- Delete Deal Modal (v5.5 #11) -------------------- */
function DeleteDealModal({ deal, onCancel, onConfirmed }) {
  const term = useTerm();
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const border = { borderColor: "var(--cw-border)" };
  const submit = async () => {
    if (text.trim() !== deal.company_name) {
      toast.error("Confirmation text does not match the company name");
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/deals/${deal.deal_id}`, { data: { confirmation_text: text.trim(), reason } });
      toast.success(`${term("Deal")} deleted`);
      onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="delete-deal-modal">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="px-6 py-4 border-b" style={border}>
          <h2 className="cw-h2 text-[var(--cw-error)]">Delete {term("deal").toLowerCase()}</h2>
          <p className="text-xs text-[var(--cw-muted)] mt-1">
            This will remove the {term("deal").toLowerCase()} from your workspace views. Historical logs remain in the database.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <div className="cw-caption">Type the company name to confirm</div>
            <div className="text-xs text-[var(--cw-muted)] mt-1">Company name: <strong>{deal.company_name}</strong></div>
            <input value={text} onChange={(e) => setText(e.target.value)}
              className="w-full h-9 px-3 rounded-md border text-sm mt-2" style={border}
              data-testid="delete-confirm-input" />
          </div>
          <div>
            <div className="cw-caption">Reason (optional)</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full min-h-[70px] px-3 py-2 rounded-md border text-sm mt-1" style={border}
              data-testid="delete-reason-input" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2" style={border}>
          <button onClick={onCancel} className="cw-btn-ghost text-sm">Cancel</button>
          <button
            disabled={busy || text.trim() !== deal.company_name}
            onClick={submit}
            className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border transition disabled:opacity-40"
            style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)", background: "rgba(214,69,69,0.06)" }}
            data-testid="delete-confirm-btn">
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* -------------------- v5.7 Addition 15: Interactive Agent Conversation -------------------- */
function AgentConversation({ runId, initialOutput }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/agent-runs/${runId}/messages`);
    setMessages(data);
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/agent-runs/${runId}/messages`, { message_text: text.trim() });
      setText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2" data-testid={`agent-convo-${runId}`}>
      {messages.length === 0 && initialOutput && (
        <div className="p-3 rounded-md text-sm whitespace-pre-wrap" style={{ background: "rgba(40,42,116,0.06)" }}>
          {initialOutput}
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {messages.map((m) => (
          <div
            key={m.message_id}
            className={`p-3 rounded-md text-sm whitespace-pre-wrap ${m.sender === "user" ? "ml-8" : "mr-8"}`}
            style={{
              background: m.sender === "user" ? "rgba(0,0,0,0.04)" : "rgba(40,42,116,0.06)",
            }}
            data-testid={`msg-${m.sender}-${m.message_id}`}
          >
            <div className="text-[10px] uppercase text-[var(--cw-muted)] mb-1">
              {m.sender === "user" ? "You" : "Agent"} · {m.created_at}
            </div>
            {m.message_text}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask a follow-up…"
          className="flex-1 h-9 px-3 rounded-md border text-sm"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid={`agent-convo-input-${runId}`}
        />
        <button
          onClick={send}
          disabled={busy}
          className="cw-btn-primary"
          data-testid={`agent-convo-send-${runId}`}
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

/* -------------------- v5.7 Addition 16: Multi-format Output Files -------------------- */
function RunOutputFiles({ runId }) {
  const [files, setFiles] = useState([]);
  useEffect(() => {
    api.get(`/agent-runs/${runId}/output-files`).then(({ data }) => setFiles(data)).catch(() => {});
  }, [runId]);
  if (files.length === 0) return null;
  return (
    <div className="mt-4 border-t pt-3" style={{ borderColor: "var(--cw-border)" }}>
      <div className="cw-caption mb-2">Output Files</div>
      <div className="flex flex-wrap gap-2" data-testid={`run-files-${runId}`}>
        {files.map((f) => (
          <a
            key={f.output_file_id}
            href={f.file_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs hover:bg-black/5"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid={`run-file-${f.file_type}`}
          >
            <FileText size={12} />
            {f.file_name}
            <span className="uppercase text-[10px] text-[var(--cw-muted)]">{f.file_type}</span>
          </a>
        ))}
      </div>
    </div>
  );
}


/* -------------------- v5.10 A3: Agentic Gates Tab -------------------- */
function AgenticGatesTab({ dealId, isReadOnly }) {
  const nav = useNavigate();
  const term = useTerm();
  const [gates, setGates] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/deals/${dealId}/gates`);
      setGates(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load gates");
    }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  const runGate = async (gate_id) => {
    setBusy(gate_id);
    try {
      const { data } = await api.post(`/deals/${dealId}/gates/${gate_id}/run`);
      toast.success("AutoGate run started");
      nav(`/gate-runs/${data.run_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start gate run");
    } finally {
      setBusy(null);
    }
  };

  // v5.11 A1.6 — direct manual entry, no AI
  const runManual = async (gate_id) => {
    setBusy(gate_id);
    try {
      const { data } = await api.post(`/deals/${dealId}/gates/${gate_id}/run-manual`);
      toast.success("Manual gate run started");
      nav(`/gate-runs/${data.run_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start manual run");
    } finally {
      setBusy(null);
    }
  };

  if (gates.length === 0) {
    return (
      <div className="cw-card p-10 text-center text-sm text-[var(--cw-muted)]">
        No gates mapped to this pipeline. Map a gate under Settings ▸ {term("Gate")}-{term("Stage")} Mapping.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="agentic-gates-tab">
      {gates.map((g) => {
        const lr = g.latest_run;
        const status = lr?.status;
        const displayStatus = status === "awaiting_confirmation" ? "Awaiting confirmation"
          : status === "in_progress" ? "Manual in progress"
          : status === "completed" ? "Completed"
          : status === "running" || status === "queued" ? "Scoring…"
          : status === "failed" ? "Failed" : "Not run";
        const statusColor = {
          "Awaiting confirmation": { bg: "rgba(243,191,92,0.18)", color: "#8A6510" },
          "Manual in progress": { bg: "rgba(243,191,92,0.18)", color: "#8A6510" },
          Completed: { bg: "rgba(44,154,82,0.14)", color: "#1F6E3A" },
          "Scoring…": { bg: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" },
          Failed: { bg: "rgba(214,69,69,0.12)", color: "var(--cw-error)" },
          "Not run": { bg: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" },
        }[displayStatus];
        const openLatest = () => nav(`/gate-runs/${lr.run_id}`);
        return (
          <div key={g.gate_id} className="cw-card p-5" data-testid={`gate-row-${g.gate_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex-1 min-w-[300px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="cw-h3">{g.gate_name}</div>
                  <span className="cw-badge" style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
                    {g.gate_type === "hard_filter" ? "Hard Filter" : "Weighted"}
                  </span>
                  {g.auto_run_on_entry && (
                    <span className="cw-badge" style={{ background: "rgba(44,154,82,0.10)", color: "#1F6E3A" }}
                      title="Automatically runs when a deal enters the mapped stage">Auto-run</span>
                  )}
                </div>
                <div className="text-xs text-[var(--cw-muted)] mt-1">
                  {g.criteria_count} criteria
                  {lr && ` · Latest run: ${lr.created_at}`}
                  {lr?.triggered_by_name && ` · by ${lr.triggered_by_name}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="cw-badge" style={statusColor}>{displayStatus}</span>
                {!isReadOnly && (
                  <>
                    {(status === "awaiting_confirmation" || status === "completed" || status === "in_progress") && (
                      <button onClick={openLatest}
                        className={status === "completed" ? "cw-btn-ghost text-sm" : "cw-btn-secondary text-sm"}
                        data-testid={`gate-action-${g.gate_id}`}>
                        {status === "in_progress" ? "Continue manual" : status === "awaiting_confirmation" ? "Continue review" : "View result"}
                      </button>
                    )}
                    <button onClick={() => runManual(g.gate_id)}
                      disabled={busy === g.gate_id}
                      className="cw-btn-ghost text-sm inline-flex items-center gap-1"
                      data-testid={`gate-run-manual-${g.gate_id}`}
                      title="Fill in the gate directly, no AI">
                      Run Manually
                    </button>
                    <button onClick={() => runGate(g.gate_id)}
                      disabled={busy === g.gate_id || status === "queued" || status === "running"}
                      className="cw-btn-primary text-sm inline-flex items-center gap-1"
                      data-testid={`gate-run-${g.gate_id}`}>
                      <Play size={12} /> {busy === g.gate_id ? "Starting…" : "Run with AutoGate"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
